/**
 * Social share buttons for blog articles.
 *
 * What: Renders share buttons for WhatsApp, Telegram, Facebook, X, LinkedIn, Email, and Copy Link.
 *       On mobile, a primary "Share" button triggers the native Web Share API for maximum reach.
 * Why: Increases blog post distribution through parent/student social channels (WhatsApp groups, Telegram, etc.).
 * When: Update when adding new share targets or changing share button layout.
 */
import React, { useCallback, useState } from 'react';
import {
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  IosShare as IosShareIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { colors, radii } from '@/components/landing/landingTokens';
import {
  shareNative,
  supportsNativeShare as canShareNatively,
  whatsAppShareUrl,
} from '@/utils/nativeShare';

interface BlogShareButtonsProps {
  url: string;
  title: string;
  description: string;
  /** 'inline' = compact row (header area), 'block' = prominent section (end of article) */
  variant?: 'inline' | 'block';
}

/* ------------------------------------------------------------------ */
/*  SVG icons for platforms without MUI built-ins                     */
/* ------------------------------------------------------------------ */

function WhatsAppIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function TelegramIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function XIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function LinkedInIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function InstagramIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z" />
    </svg>
  );
}

function ThreadsIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.17.408-2.243 1.33-3.023.88-.744 2.084-1.168 3.59-1.264 1.104-.07 2.134-.03 3.066.117-.08-.422-.205-.794-.378-1.108-.454-.83-1.282-1.263-2.39-1.288h-.06c-.856 0-1.862.293-2.378.91l-1.558-1.378c.873-.986 2.177-1.532 3.672-1.54h.088c1.728.019 3.063.686 3.868 1.93.348.54.597 1.168.748 1.875a12.09 12.09 0 012.845 1.348c.915.612 1.637 1.39 2.112 2.312.777 1.51.85 4.058-1.227 6.105-1.782 1.755-4.004 2.577-7.066 2.605zm-.478-6.281c.085.002.17.003.254.003.98 0 1.75-.283 2.29-.84.477-.496.776-1.19.894-2.06-.75-.207-1.578-.33-2.472-.33-2.017 0-3.285.692-3.378 1.843-.053.652.353 1.262 1.079 1.62.509.25 1.12.381 1.744.381l-.41-.617z" />
    </svg>
  );
}

function TikTokIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Share URL builders                                                */
/* ------------------------------------------------------------------ */

function buildWhatsAppUrl(url: string, title: string) {
  return whatsAppShareUrl(`${title}\n${url}`);
}

function buildTelegramUrl(url: string, title: string) {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
}

function buildFacebookUrl(url: string) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

function buildXUrl(url: string, title: string) {
  return `https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
}

function buildLinkedInUrl(url: string) {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

function buildEmailUrl(url: string, title: string, description: string) {
  return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}\n\nRead more: ${url}`)}`;
}

function buildThreadsUrl(url: string, title: string) {
  return `https://www.threads.net/intent/post?text=${encodeURIComponent(`${title}\n${url}`)}`;
}

function buildInstagramUrl(url: string, title: string) {
  // Instagram doesn't have a direct share-to-feed URL; stories link is the closest web intent
  // On mobile this opens the app; on desktop it opens instagram.com
  return `https://www.instagram.com/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
}

function buildTikTokUrl(url: string, title: string) {
  // TikTok has no direct web share intent; open the URL in a way that lets users copy/share
  return `https://www.tiktok.com/share?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
}

/* ------------------------------------------------------------------ */
/*  Share channel definitions                                         */
/* ------------------------------------------------------------------ */

interface ShareChannel {
  key: string;
  label: string;
  icon: React.ReactNode;
  getUrl: (url: string, title: string, description: string) => string;
  color: string;
}

const SHARE_CHANNELS: ShareChannel[] = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: <WhatsAppIcon />,
    getUrl: (url, title) => buildWhatsAppUrl(url, title),
    color: '#25D366',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    icon: <TelegramIcon />,
    getUrl: (url, title) => buildTelegramUrl(url, title),
    color: '#0088cc',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: <FacebookIcon />,
    getUrl: url => buildFacebookUrl(url),
    color: '#1877F2',
  },
  {
    key: 'x',
    label: 'X',
    icon: <XIcon />,
    getUrl: (url, title) => buildXUrl(url, title),
    color: '#000000',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    icon: <LinkedInIcon />,
    getUrl: url => buildLinkedInUrl(url),
    color: '#0A66C2',
  },
  {
    key: 'threads',
    label: 'Threads',
    icon: <ThreadsIcon />,
    getUrl: (url, title) => buildThreadsUrl(url, title),
    color: '#000000',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: <InstagramIcon />,
    getUrl: (url, title) => buildInstagramUrl(url, title),
    color: '#E4405F',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    icon: <TikTokIcon />,
    getUrl: (url, title) => buildTikTokUrl(url, title),
    color: '#000000',
  },
  {
    key: 'email',
    label: 'Email',
    icon: <EmailIcon sx={{ fontSize: 20 }} />,
    getUrl: (url, title, description) => buildEmailUrl(url, title, description),
    color: colors.inkMuted,
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function BlogShareButtons({
  url,
  title,
  description,
  variant = 'block',
}: BlogShareButtonsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copiedOpen, setCopiedOpen] = useState(false);

  const supportsNativeShare = canShareNatively({
    title,
    text: description,
    url,
  });

  // A cancelled or refused share needs no follow-up here: the channel buttons
  // stay on screen as the fallback.
  const handleNativeShare = useCallback(() => {
    void shareNative({ title, text: description, url });
  }, [title, description, url]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedOpen(true);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedOpen(true);
    }
  }, [url]);

  const handleShareClick = useCallback(
    (channel: ShareChannel) => {
      const shareUrl = channel.getUrl(url, title, description);
      if (channel.key === 'email') {
        window.location.href = shareUrl;
      } else {
        window.open(
          shareUrl,
          '_blank',
          'noopener,noreferrer,width=600,height=500'
        );
      }
    },
    [url, title, description]
  );

  /* ---------- Inline variant (compact, in header area) ---------- */
  if (variant === 'inline') {
    return (
      <>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
        >
          {/* Native share on mobile — most prominent */}
          {isMobile && supportsNativeShare && (
            <Button
              size="small"
              startIcon={<IosShareIcon sx={{ fontSize: '16px !important' }} />}
              onClick={handleNativeShare}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: colors.accentOrange,
                borderRadius: radii.pill,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.paper,
                px: 1.5,
                py: 0.25,
                minHeight: 32,
                '&:hover': {
                  backgroundColor: colors.backgroundAlt,
                },
              }}
            >
              Share
            </Button>
          )}

          {/* Individual channel buttons */}
          {SHARE_CHANNELS.map(channel => (
            <Tooltip key={channel.key} title={channel.label} arrow>
              <IconButton
                size="small"
                onClick={() => handleShareClick(channel)}
                aria-label={`Share on ${channel.label}`}
                sx={{
                  width: 32,
                  height: 32,
                  color: colors.inkSubtle,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    color: channel.color,
                    backgroundColor: `${channel.color}12`,
                  },
                }}
              >
                {channel.icon}
              </IconButton>
            </Tooltip>
          ))}

          {/* Copy link */}
          <Tooltip title="Copy link" arrow>
            <IconButton
              size="small"
              onClick={handleCopyLink}
              aria-label="Copy link"
              sx={{
                width: 32,
                height: 32,
                color: colors.inkSubtle,
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: colors.accentCobalt,
                  backgroundColor: `${colors.accentCobalt}12`,
                },
              }}
            >
              <CopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Snackbar
          open={copiedOpen}
          autoHideDuration={2000}
          onClose={() => setCopiedOpen(false)}
          message="Link copied"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </>
    );
  }

  /* ---------- Block variant (prominent, end of article) ---------- */
  return (
    <>
      <Box
        sx={{
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
          py: { xs: 3, sm: 3.5 },
          px: { xs: 0, sm: 0 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '1rem', sm: '1.0625rem' },
            fontWeight: 700,
            color: colors.ink,
            mb: { xs: 1.5, sm: 2 },
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          Found this helpful? Share it with someone who needs it.
        </Typography>

        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 1.25 },
            justifyContent: { xs: 'center', sm: 'flex-start' },
          }}
        >
          {/* Native share — most prominent on mobile */}
          {supportsNativeShare && (
            <Button
              variant="contained"
              startIcon={<IosShareIcon />}
              onClick={handleNativeShare}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                borderRadius: radii.pill,
                backgroundColor: colors.accentOrange,
                // §10 small-orange rule: below 19px the label takes ink (5.3:1),
                // not white (3.1:1).
                color: colors.ink,
                px: 2.5,
                py: 0.75,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: colors.accentOrangeLight,
                  boxShadow: 'none',
                },
                // Full width on mobile for easy thumb tap
                ...(isMobile && {
                  width: '100%',
                  py: 1.25,
                  fontSize: '0.9375rem',
                  mb: 0.5,
                }),
              }}
            >
              Share this article
            </Button>
          )}

          {/* WhatsApp & Telegram — prominent pill buttons (key channels for parents) */}
          {SHARE_CHANNELS.filter(
            c => c.key === 'whatsapp' || c.key === 'telegram'
          ).map(channel => (
            <Button
              key={channel.key}
              startIcon={channel.icon}
              onClick={() => handleShareClick(channel)}
              aria-label={`Share on ${channel.label}`}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                borderRadius: radii.pill,
                border: `1.5px solid ${channel.color}30`,
                color: channel.color,
                backgroundColor: `${channel.color}08`,
                px: 2,
                py: 0.75,
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: `${channel.color}15`,
                  borderColor: `${channel.color}50`,
                },
                // Half width on mobile
                ...(isMobile && {
                  flex: '1 1 calc(50% - 4px)',
                  py: 1,
                }),
              }}
            >
              {channel.label}
            </Button>
          ))}

          {/* Other social channels — icon buttons in a row */}
          {SHARE_CHANNELS.filter(
            c => c.key !== 'whatsapp' && c.key !== 'telegram'
          ).map(channel => (
            <Tooltip key={channel.key} title={channel.label} arrow>
              <IconButton
                onClick={() => handleShareClick(channel)}
                aria-label={`Share on ${channel.label}`}
                sx={{
                  width: { xs: 44, sm: 40 },
                  height: { xs: 44, sm: 40 },
                  border: `1px solid ${colors.border}`,
                  borderRadius: radii.pill,
                  color: colors.inkSubtle,
                  backgroundColor: colors.paper,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    color: channel.color,
                    borderColor: `${channel.color}40`,
                    backgroundColor: `${channel.color}08`,
                  },
                }}
              >
                {channel.icon}
              </IconButton>
            </Tooltip>
          ))}

          {/* Copy link button */}
          <Tooltip title="Copy link" arrow>
            <IconButton
              onClick={handleCopyLink}
              aria-label="Copy link"
              sx={{
                width: { xs: 44, sm: 40 },
                height: { xs: 44, sm: 40 },
                border: `1px solid ${colors.border}`,
                borderRadius: radii.pill,
                color: colors.inkSubtle,
                backgroundColor: colors.paper,
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: colors.accentCobalt,
                  borderColor: `${colors.accentCobalt}40`,
                  backgroundColor: `${colors.accentCobalt}08`,
                },
              }}
            >
              <CopyIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Snackbar
        open={copiedOpen}
        autoHideDuration={2000}
        onClose={() => setCopiedOpen(false)}
        message="Link copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
